import React, { useEffect } from 'react';
import './Portfolio.css';

export default function Portfolio(){
  useEffect(()=>{
    document.title = 'Portfolio — SocialManiacs';
  },[]);

  // Lazy-load videos when they come into view to reduce initial network load
  useEffect(()=>{
    const videos = Array.from(document.querySelectorAll('video.lazy-video'));

    function loadVideo(v){
      const srcEl = v.querySelector('source[data-src]');
      if (srcEl && !srcEl.src) {
        srcEl.src = srcEl.getAttribute('data-src');
        v.load();
      }
    }

    if (!('IntersectionObserver' in window)){
      videos.forEach(loadVideo);
      return;
    }

    const observer = new IntersectionObserver((entries)=>{
      entries.forEach(({isIntersecting, target})=>{
        if(isIntersecting){
          loadVideo(target);
          observer.unobserve(target);
        }
      })
    },{ rootMargin: '200px 0px', threshold: 0.01 });

    videos.forEach(v=>observer.observe(v));
    return ()=>observer.disconnect();
  },[]);

  // Ensure only one video plays at a time
  useEffect(()=>{
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => {
      video.addEventListener('play', () => {
        allVideos.forEach(otherVideo => {
          if (otherVideo !== video) {
            otherVideo.pause();
          }
        });
      });
    });
  },[]);

  // Try to import images bundled from src/assets/portfolio_img
  const assetImages = (() => {
    try {
      const r = require.context('../assets/portfolio_img', false, /\.(png|jpe?g|gif|webp)$/i);
      return r.keys().map((key) => ({
        src: r(key),
        title: key.replace(/^\.\//, '').replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
      }));
    } catch (e) {
      return [];
    }
  })();

  const images = [
    'i5',
    'i6',
    'i7',
    'i8',
    'Nov Story 5.jpg',
    'Oct Story 10.jpg',
    'Oct Story 2.jpg',
    'Oct story 3.jpg',
    'Oct story 4.jpg',
    'Persang Post Dec 2.jpg',
    'Persang Post Dec 3.jpg',
    'Post -Agrawal Dairy 6.jpg',
    'Post 2.jpg',
    'Post 3.jpeg',
    'Post 3A.jpg',
    'Post 3B.jpg',
    'Post 3C.jpg',
    'Post 3D.jpg',
    'Post 3E.jpg',
    'Post October 2.jpg',
    'Post Recruiting Hub - Jan 6.jpg',
    'Post Skyline Dec - 1.jpg',
    'Post Skyline Dec - 2.jpg',
    'Sept Story 5.jpg',
    'Skyline Post - 4.jpg',
    'Story 3.jpg',
    'Story 4.jpg',
    'Story 6 (1).jpg',
    'Story 6.jpg',
    'Story 7 (1).jpg',
    'Story 7.jpg',
    'Story 8.jpg',
    'Story 9.jpg',
    'recruiting hub story feb-01.jpg',
    'recruiting jan 2.1-05.jpg',
    'April Story 1.jpeg',
    'IPL Post 1.jpg',
    'July Post 2.jpg',
    'July Story 9.jpg',
    'Karelibuag Post 3.jpg',
    'Krishiv Post 3.jpg',
    'Luxuria Post 1.jpg',
    'Mothers Day Story CA Hitesh.jpg',
    'i1',
    'i2',
    'i3',
    'i4',
    'i9'
  ];


  const toTitle = (filename) => filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');

  const videoFiles = [
    'Influencer_Deeds_1.webm',
    'Influencer_Deeds_2.webm',
    'Influencer_Deeds_3.webm',
    'Influencer_Deeds_4.webm',
    'Influencer_Deeds_5.webm',
    'Influencer_Deeds_6.webm',
  ];
const video = [ 
  'video1.mp4',
    'video2.mp4',
    'VideoShoots1.webm',
    'VideoShoots2.webm',
    'VideoShoots3.webm',
    'VideoShoots4.webm',
    'VideoShoots5.webm',
    'VideoShoots6.webm',
    'VideoShoots7.webm',
    'VideoShoots8.webm',
    'VideoShoots9.webm',
    'VideoShoots10.webm',
   
  ];
  const graphicPrintFiles = [
    'CA Akash deck.pdf',
    'Recruiting Hub Brochure.pdf',
    'SchoolStandee.jpg',
    'Standee-2 - print.pdf'
  ];
  return (
    <div>
      <section id="portfolio" className="page-header">
        <div className="page-min-bg" aria-hidden="true">
          <svg className="bg-lines" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M -50 200 Q 100 100, 300 50" className="bg-arc" />
            <path d="M 700 -50 Q 850 100, 1000 300" className="bg-arc" />
            <path d="M 400 300 Q 600 250, 800 300" className="bg-arc center" />
          </svg>
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="blob blob-3" />
          <div className="ring ring-1" />
          <div className="dots">
            <span className="dot dot-1" />
            <span className="dot dot-2" />
            <span className="dot dot-3" />
            <span className="dot dot-4" />
            <span className="dot dot-5" />
          </div>
        </div>
        <div className="container">
          <div className="text-center mb-4 scroll-reveal">
            <h1 className="fw-bold">Designing Instinct</h1>
          </div>
          {(() => {
            const source = assetImages.length ? assetImages : images;
            const toObj = (it) => typeof it === 'object'
              ? it
              : { src: `${process.env.PUBLIC_URL}/images/portfolio_img/${encodeURIComponent(it)}`, title: toTitle(it) };

            const items = source.map(toObj);
            
            // Separate featured images (i5, i6, i7, i8) to show them first
            const featuredNames = ['i5', 'i6', 'i7', 'i8'];
            const featured = featuredNames
              .map(name => items.find(item => 
                item.title.includes(name) || item.src.includes(name)
              ))
              .filter(Boolean);
            
            const other = items.filter(item =>
              !featured.includes(item)
            );
            
            const curated = [...featured, ...other].slice(0, 29);

            return (
              <div className="row g-4">
                {curated.map((it) => (
                  <div className="col-12 col-md-6 col-lg-4 scroll-reveal" key={it.src}>
                    <div className="portfolio-item shadow-sm">
                      <div className="portfolio-thumb">
                        <img src={it.src} alt={it.title} loading="lazy" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>
      

      <div className="section-sep" />

      <section id="reels" className="portfolio-section">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="fw-bold">Video Production</h2>
          </div>
          <div className="row g-4">
            <div className="row g-4">
      {video && video.length ? (
        video.map((name) => {
          const vidId = `vid-${name.replace(/[^a-z0-9]+/gi,'-')}`;
          return (
          <div className="col-12 col-md-6 col-lg-4" key={name}>
            <div className="portfolio-item shadow-sm">
              <div className="portfolio-thumb">
                <video
                  id={vidId}
                  className="lazy-video"
                  controls
                  playsInline
                  preload="none"
                  style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '10px' }}
                  onDoubleClick={(e)=>{
                    const el = e.currentTarget;
                    if (document.fullscreenElement) {
                      document.exitFullscreen && document.exitFullscreen();
                    } else if (el.requestFullscreen) {
                      el.requestFullscreen();
                    } else if (el.webkitEnterFullscreen) {
                      el.webkitEnterFullscreen();
                    }
                  }}
                >
                  <source
                    data-src={`${process.env.PUBLIC_URL}/video/${encodeURIComponent(name)}`}
                    type="video/webm"
                  />
                  Your browser does not support the video tag.
                </video>
                <button
                  type="button"
                  className="video-fullscreen-btn"
                  aria-label="Fullscreen"
                  onClick={()=>{
                    const el = document.getElementById(vidId);
                    if (!el) return;
                    if (document.fullscreenElement) {
                      document.exitFullscreen && document.exitFullscreen();
                    } else if (el.requestFullscreen) {
                      el.requestFullscreen();
                    } else if (el.webkitEnterFullscreen) {
                      el.webkitEnterFullscreen();
                    }
                  }}
                >⛶</button>
              </div>
              
            </div>
          </div>
        )})
      ) : (
        <div className="col-12 text-center text-muted">No videos to show yet.</div>
      )}
    </div>
          </div>
        </div>
      </section>
      <section id="graphic-print" className="portfolio-section">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="fw-bold">Print Graphics</h2>
          </div>
          <div className="row g-4">
            {graphicPrintFiles && graphicPrintFiles.length ? (
              graphicPrintFiles.map((name) => {
                const isImage = /\.(png|jpe?g|gif|webp)$/i.test(name);
                const href = `${process.env.PUBLIC_URL}/pdf/${encodeURIComponent(name)}`;
                return (
                  <div className="col-12 col-md-6 col-lg-4" key={name}>
                    <div className="portfolio-item shadow-sm">
                      <div className="portfolio-thumb">
                        {isImage ? (
                          <img src={href} alt={toTitle(name)} loading="lazy" />
                        ) : (
                          <div className="pdf-embed-container">
                            <iframe src={`${href}#toolbar=0&navpanes=0&scrollbar=0`} title={toTitle(name)} loading="lazy"></iframe>
                            <a
                              className="btn btn-sm btn-outline-primary pdf-view-btn"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-box-arrow-up-right me-2"></i>View Full
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-12 text-center text-muted">No print assets to show yet.</div>
            )}
          </div>
        </div>
      </section>
      <div className="section-sep" />
<section id="videos" className="portfolio-section">
  <div className="container">
    <div className="text-center mb-4">
      <h2 className="fw-bold">Influencer Marketing</h2>
    </div>
    <div className="row g-4">
      {videoFiles && videoFiles.length ? (
        videoFiles.map((name) => {
          const vidId = `vid-${name.replace(/[^a-z0-9]+/gi,'-')}`;
          return (
          <div className="col-12 col-md-6 col-lg-4" key={name}>
            <div className="portfolio-item shadow-sm">
              <div className="portfolio-thumb">
                <video
                  id={vidId}
                  className="lazy-video"
                  controls
                  playsInline
                  preload="none"
                  style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '10px' }}
                  onDoubleClick={(e)=>{
                    const el = e.currentTarget;
                    if (document.fullscreenElement) {
                      document.exitFullscreen && document.exitFullscreen();
                    } else if (el.requestFullscreen) {
                      el.requestFullscreen();
                    } else if (el.webkitEnterFullscreen) {
                      el.webkitEnterFullscreen();
                    }
                  }}
                >
                  <source
                    data-src={`${process.env.PUBLIC_URL}/video/${encodeURIComponent(name)}`}
                    type="video/webm"
                  />
                  Your browser does not support the video tag.
                </video>
                <button
                  type="button"
                  className="video-fullscreen-btn"
                  aria-label="Fullscreen"
                  onClick={()=>{
                    const el = document.getElementById(vidId);
                    if (!el) return;
                    if (document.fullscreenElement) {
                      document.exitFullscreen && document.exitFullscreen();
                    } else if (el.requestFullscreen) {
                      el.requestFullscreen();
                    } else if (el.webkitEnterFullscreen) {
                      el.webkitEnterFullscreen();
                    }
                  }}
                >⛶</button>
              </div>
              
            </div>
          </div>
        )})
      ) : (
        <div className="col-12 text-center text-muted">No videos to show yet.</div>
      )}
    </div>
  </div>
</section>
      <div className="section-sep" />
    </div>
  );
}